// EN/FR copy for Evaluation Responsibility duties: the drill-down in the manager's criterion,
// the Work Center duty ledger and the admin integrity view. The criterion's own copy lives in
// routineCopy.responsibility. Canadian English/French.

const plural = (n, one, many) => (n === 1 ? one : many);

const COPY = {
    en: {
        locale: "en-CA",
        loading: "Loading…",
        retry: "Try again",
        showDuties: n => `View ${n} ${plural(n, "duty", "duties")}`,
        hideDuties: "Hide duties",
        sources: {
            PEER_FEEDBACK: "Peer feedback", CORE_REVIEW: "Core Responsibilities review", GOAL_DECISION: "Goal proposal decision",
            GOAL_REVIEW: "Goal final evaluation", ADDITIONAL_CONTRIBUTION_REVIEW: "Contribution review", KPI_SUBMISSION: "KPI submission",
            KPI_APPROVAL: "KPI approval", UNIT_QUALITATIVE_REVIEW: "Unit qualitative review",
        },
        states: { OVERDUE: "Overdue", DUE_SOON: "Due soon", OPEN: "Open", ON_TIME: "On time", COMPLETED_LATE: "Completed late", EXEMPT: "Exempt", TRANSFERRED: "Transferred", CANCELLED: "Cancelled" },
        groups: { open: "To do", done: "Completed", closed: "Exempt, transferred or cancelled" },
        due: date => `Due ${date}`,
        completed: date => `Completed ${date}`,
        revision: n => `Review cycle ${n + 1}`,
        forPerson: name => `For ${name}`,
        transferred: "Reassigned",
        open: "Open",
        ledger: {
            tab: "Evaluation Duties",
            intro: "Reviews, approvals and feedback you owe others this period. This is the record your manager sees when rating your Evaluation Responsibility.",
            summary: (open, overdue) => `${open} open${overdue ? ` · ${overdue} overdue` : ""}`,
            empty: ["No evaluation duties", "Nothing has been assigned to you to review, approve or give feedback on this period."],
            error: "Your evaluation duties could not be loaded.",
        },
        dashboard: {
            eyebrow: "Evaluation & Reviews",
            title: "Evaluation Accountability",
            subtitle: "Your obligations in this period’s evaluation processes: the reviews, approvals and feedback others rely on. Tracked separately from your CVI results; it is not an additional score.",
            status: { verified: "Verified", provisional: "Verification pending", pending_empty: "Verification pending", not_applicable: "Not applicable", exempt: "Exempt", error: "Unavailable" },
            assigned: "Assigned", completed: "Completed", overdue: "Overdue",
            completedHint: (onTime, late, none) => none ? "None yet" : late ? `${onTime} on time · ${late} late` : `${onTime} on time`,
            assignedHint: open => `${open} still open`,
            overdueHint: soon => soon ? `${soon} due within 7 days` : "None due within 7 days",
            onTimeTitle: "On-time completion",
            onTimeHint: (onTime, due) => `${onTime} of ${due} duties due so far were completed on time`,
            onTimeNone: "No duty has reached its deadline yet.",
            provisional: {
                RECORDS_INCOMPLETE: "Provisional: your organization’s records for this period are still being verified, so these counts may be incomplete. Rates appear once the period is verified.",
                PERIOD_NOT_ELIGIBLE: "Provisional: this period is not covered by verified tracking, so these counts may be incomplete. They are shown for information only.",
            },
            pendingEmpty: ["No duties recorded yet", "Nothing is recorded for you in this period, but its records are not yet verified, so this does not confirm that you have no evaluation duties."],
            notApplicable: ["No evaluation duties this period", "No reviews, approvals or feedback were assigned to you in this verified period."],
            exempt: ["All duties exempted", "An administrator exempted all of your evaluation duties this period."],
            error: "Your evaluation accountability could not be loaded.",
            noPeriod: "There is no active period.",
            nextOverdue: "Overdue",
            nextDue: "Next due",
            closed: (transferred, exempt) => [transferred && `${transferred} transferred`, exempt && `${exempt} exempt`].filter(Boolean).join(" · "),
            closedNote: "Not counted in your assigned duties.",
            details: "View accountability details",
            drawerSubtitle: period => `${period} · your duties only`,
        },
        admin: {
            tab: "Evaluation integrity",
            intro: "Checks that every review, approval and feedback duty implied by this period’s workflows is registered with an owner, deadline and audit trail. Evaluation Responsibility can only be rated in a certified period.",
            period: "Period",
            status: "Certification", certified: "Certified", notCertified: "Not certifiable",
            expected: "Expected duties", registered: "Registered duties",
            eligibility: "Eligibility",
            eligible: "Period is active or finalizing and starts after the configured tracking cutover.",
            ineligible: (status, cutover) => cutover
                ? `Not eligible: the period is ${String(status || "").toLowerCase()} or started before tracking cutover. Closed and pre-cutover periods are never recertified.`
                : "Not eligible: no tracking cutover date is configured, so no period can be certified yet.",
            blockers: "What blocks certification",
            noBlockers: "No blockers. All expected duties are registered and valid.",
            categories: {
                UNREGISTERED: "Duties implied by workflows but not registered (typically created before tracking or imported directly)",
                UNRECONCILED: "Registered duties with no matching workflow source or owner",
                ASSIGNMENT_CONFLICT: "Unit qualitative questions without exactly one assigned evaluator",
                OWNER: "Missing direct manager or inactive duty owner",
                KPI_SCHEDULE: "KPI schedule or occurrence gaps",
                RECORD: "Invalid registry records (window, status or audit trail)",
                PERIOD: "Period not eligible for certification",
            },
            bySource: "Unregistered duties by source",
            readOnly: "Read-only diagnostic. Nothing is backfilled or changed from this view; historical gaps are never filled with invented assignments.",
            loadError: "The integrity report could not be loaded.",
            noPeriods: "No periods are available.",
        },
    },
    fr: {
        locale: "fr-CA",
        loading: "Chargement…",
        retry: "Réessayer",
        showDuties: n => `Voir ${n} tâche${n > 1 ? "s" : ""}`,
        hideDuties: "Masquer les tâches",
        sources: {
            PEER_FEEDBACK: "Rétroaction des collègues", CORE_REVIEW: "Revue des responsabilités principales", GOAL_DECISION: "Décision sur une proposition d’objectif",
            GOAL_REVIEW: "Évaluation finale d’objectif", ADDITIONAL_CONTRIBUTION_REVIEW: "Revue de contribution", KPI_SUBMISSION: "Saisie d’indicateur",
            KPI_APPROVAL: "Approbation d’indicateur", UNIT_QUALITATIVE_REVIEW: "Revue qualitative d’unité",
        },
        states: { OVERDUE: "En retard", DUE_SOON: "Échéance proche", OPEN: "En cours", ON_TIME: "Dans les délais", COMPLETED_LATE: "Terminée en retard", EXEMPT: "Exemptée", TRANSFERRED: "Transférée", CANCELLED: "Annulée" },
        groups: { open: "À faire", done: "Terminées", closed: "Exemptées, transférées ou annulées" },
        due: date => `Échéance ${date}`,
        completed: date => `Terminée le ${date}`,
        revision: n => `Cycle de revue ${n + 1}`,
        forPerson: name => `Pour ${name}`,
        transferred: "Réattribuée",
        open: "Ouvrir",
        ledger: {
            tab: "Responsabilités d’évaluation",
            intro: "Revues, approbations et rétroactions que vous devez à d’autres cette période. C’est le registre que votre gestionnaire consulte pour noter votre responsabilité d’évaluation.",
            summary: (open, overdue) => `${open} en cours${overdue ? ` · ${overdue} en retard` : ""}`,
            empty: ["Aucune tâche d’évaluation", "Rien ne vous a été attribué à revoir, à approuver ou à commenter cette période."],
            error: "Vos tâches d’évaluation n’ont pas pu être chargées.",
        },
        dashboard: {
            eyebrow: "Évaluations et suivis",
            title: "Responsabilités en matière d’évaluation",
            subtitle: "Vos obligations dans les processus d’évaluation de la période : les revues, approbations et rétroactions dont d’autres dépendent. Suivies séparément de vos résultats CVI; il ne s’agit pas d’une note supplémentaire.",
            status: { verified: "Vérifié", provisional: "Vérification en attente", pending_empty: "Vérification en attente", not_applicable: "Sans objet", exempt: "Exempté", error: "Non disponible" },
            assigned: "Attribuées", completed: "Terminées", overdue: "En retard",
            completedHint: (onTime, late, none) => none ? "Aucune pour l’instant" : late ? `${onTime} dans les délais · ${late} en retard` : `${onTime} dans les délais`,
            assignedHint: open => `${open} encore en cours`,
            overdueHint: soon => soon ? `${soon} à échéance d’ici 7 jours` : "Aucune échéance d’ici 7 jours",
            onTimeTitle: "Achèvement dans les délais",
            onTimeHint: (onTime, due) => `${onTime} des ${due} tâches échues à ce jour ont été terminées dans les délais`,
            onTimeNone: "Aucune tâche n’a encore atteint son échéance.",
            provisional: {
                RECORDS_INCOMPLETE: "Provisoire : les registres de votre organisation pour cette période sont en cours de vérification; ces chiffres peuvent donc être incomplets. Les taux s’afficheront une fois la période vérifiée.",
                PERIOD_NOT_ELIGIBLE: "Provisoire : cette période n’est pas couverte par le suivi vérifié; ces chiffres peuvent donc être incomplets. Ils sont présentés à titre d’information seulement.",
            },
            pendingEmpty: ["Aucune tâche enregistrée pour l’instant", "Rien n’est enregistré pour vous cette période, mais ses registres ne sont pas encore vérifiés; cela ne confirme donc pas que vous n’avez aucune tâche d’évaluation."],
            notApplicable: ["Aucune tâche d’évaluation cette période", "Aucune revue, approbation ou rétroaction ne vous a été attribuée dans cette période vérifiée."],
            exempt: ["Toutes les tâches exemptées", "Un administrateur a exempté toutes vos tâches d’évaluation cette période."],
            error: "Vos responsabilités en matière d’évaluation n’ont pas pu être chargées.",
            noPeriod: "Aucune période active.",
            nextOverdue: "En retard",
            nextDue: "Prochaine échéance",
            closed: (transferred, exempt) => [transferred && `${transferred} transférée${transferred > 1 ? "s" : ""}`, exempt && `${exempt} exemptée${exempt > 1 ? "s" : ""}`].filter(Boolean).join(" · "),
            closedNote: "Non comptées dans vos tâches attribuées.",
            details: "Voir le détail des responsabilités",
            drawerSubtitle: period => `${period} · vos tâches seulement`,
        },
        admin: {
            tab: "Intégrité des évaluations",
            intro: "Vérifie que chaque tâche de revue, d’approbation et de rétroaction découlant des processus de la période est enregistrée avec un responsable, une échéance et une piste d’audit. La responsabilité d’évaluation ne peut être notée que dans une période certifiée.",
            period: "Période",
            status: "Certification", certified: "Certifiée", notCertified: "Non certifiable",
            expected: "Tâches attendues", registered: "Tâches enregistrées",
            eligibility: "Admissibilité",
            eligible: "La période est active ou en finalisation et commence après la date de mise en service du suivi.",
            ineligible: (status, cutover) => cutover
                ? "Non admissible : la période est fermée ou a commencé avant la mise en service du suivi. Les périodes fermées et antérieures ne sont jamais recertifiées."
                : "Non admissible : aucune date de mise en service du suivi n’est configurée; aucune période ne peut encore être certifiée.",
            blockers: "Ce qui empêche la certification",
            noBlockers: "Aucun obstacle. Toutes les tâches attendues sont enregistrées et valides.",
            categories: {
                UNREGISTERED: "Tâches découlant des processus mais non enregistrées (souvent créées avant le suivi ou importées directement)",
                UNRECONCILED: "Tâches enregistrées sans source ou responsable correspondant",
                ASSIGNMENT_CONFLICT: "Questions qualitatives d’unité sans exactement un évaluateur attribué",
                OWNER: "Gestionnaire immédiat manquant ou responsable inactif",
                KPI_SCHEDULE: "Lacunes de calendrier ou d’occurrence d’indicateur",
                RECORD: "Enregistrements invalides (fenêtre, statut ou piste d’audit)",
                PERIOD: "Période non admissible à la certification",
            },
            bySource: "Tâches non enregistrées par source",
            readOnly: "Diagnostic en lecture seule. Rien n’est rempli ni modifié à partir de cette vue; les lacunes historiques ne sont jamais comblées par des attributions inventées.",
            loadError: "Le rapport d’intégrité n’a pas pu être chargé.",
            noPeriods: "Aucune période disponible.",
        },
    },
};

export function evaluationResponsibilityCopy(language) {
    return COPY[language === "fr" ? "fr" : "en"];
}

export const formatErDate = (value, language) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat(evaluationResponsibilityCopy(language).locale, { month: "short", day: "numeric" }).format(date);
};

// Where each duty's source task is completed, as Work Center navigation targets.
export const ER_ACTION_TARGET = {
    PEER_FEEDBACK: "peer-feedback", CORE_REVIEW: "routine-evaluation", GOAL_DECISION: "goals", GOAL_REVIEW: "goals",
    ADDITIONAL_CONTRIBUTION_REVIEW: "review-inbox", KPI_SUBMISSION: "indicators", KPI_APPROVAL: "review-inbox",
};
